import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Row, Col,
  Divider, Statistic, Tag, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import BulkUploadModal from "../../components/common/BulkUploadModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import { listAdminUsers } from "../../services/adminUsers.service";
import { PAYEE_TYPE_OPTIONS, SALARY_STATUS_OPTIONS, formatOmr } from "./constants";
import {
  listEmployeeSalaries, getEmployeeSalaryKpis, createEmployeeSalary,
  updateEmployeeSalary, deleteEmployeeSalary, exportEmployeeSalaries,
  downloadSalaryTemplate, uploadEmployeeSalaries,
} from "../../services/accounts.service";

const { TextArea } = Input;

const EARNING_FIELDS = [
  { name: "basicPay", label: "Basic Pay" },
  { name: "hra", label: "HRA" },
  { name: "tra", label: "TRA" },
  { name: "otherAllowances", label: "Other Allowances" },
  { name: "bonus", label: "Bonus" },
];

const DEDUCTION_FIELDS = [
  { name: "deductionLop", label: "Deduction / LOP" },
  { name: "otherDeductions", label: "Other Deductions" },
  { name: "socialSecurityDeductions", label: "Social Security Deductions" },
];

const STATUS_COLORS = {
  pending: "gold",
  approved: "green",
  transferred: "green",
  onHold: "orange",
  rejected: "red",
};

/**
 * Employee Salary — the entry surface for BOTH office staff and rider salaries.
 * Approval happens on Payroll › Salary Status, over the same records.
 */
export default function EmployeeSalaryTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts");
  const { options: paymentModeOptions } = useSettingsDropdown("payment-modes");
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [staffOptions, setStaffOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [payeeType, setPayeeType] = useState();
  const [status, setStatus] = useState();
  const [period, setPeriod] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [formPayeeType, setFormPayeeType] = useState("staff");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPeriod, setUploadPeriod] = useState(dayjs());
  const [netPreview, setNetPreview] = useState({ earnings: 0, deductions: 0, net: 0 });
  const [form] = Form.useForm();

  useEffect(() => {
    listAdminUsers({ page: 1, per_page: 200 })
      .then((response) => {
        const users = response?.data || [];
        setStaffOptions(
          users.map((u) => ({
            label: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email,
            value: u._id,
          }))
        );
      })
      .catch(() => setStaffOptions([]));
  }, []);

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      payeeType: payeeType || undefined,
      status: status || undefined,
      month: period ? period.month() + 1 : undefined,
      year: period ? period.year() : undefined,
      ...extra,
    }),
    [search, payeeType, status, period]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listEmployeeSalaries(params({ page, per_page: pageSize })),
        getEmployeeSalaryKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load salaries");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, payeeType, status, period]);

  /** Mirror the backend's netPay derivation so the operator sees it live. */
  const recomputeNet = useCallback(() => {
    const values = form.getFieldsValue();
    const earnings = EARNING_FIELDS.reduce((acc, f) => acc + Number(values[f.name] || 0), 0);
    const deductions = DEDUCTION_FIELDS.reduce(
      (acc, f) => acc + Number(values[f.name] || 0),
      0
    );
    setNetPreview({
      earnings: Number(earnings.toFixed(3)),
      deductions: Number(deductions.toFixed(3)),
      net: Number((earnings - deductions).toFixed(3)),
    });
  }, [form]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      setFormPayeeType(record.payeeType);
      form.setFieldsValue({
        payeeType: record.payeeType,
        rider: record.rider?._id,
        employee: record.employee?._id,
        period: dayjs(record.salaryMonth),
        workedDays: record.workedDays,
        ...Object.fromEntries(
          [...EARNING_FIELDS, ...DEDUCTION_FIELDS].map((f) => [f.name, record[f.name]])
        ),
        sourceBankAccount: record.sourceBankAccount?._id,
        paymentMode: record.paymentMode?._id,
        description: record.description,
      });
    } else {
      setFormPayeeType("staff");
      form.resetFields();
      form.setFieldsValue({ payeeType: "staff", period: dayjs() });
    }
    setTimeout(recomputeNet, 0);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { period: p, ...rest } = values;
      const payload = { ...rest, month: p.month() + 1, year: p.year() };
      if (modal.record) {
        await updateEmployeeSalary(modal.record._id, payload);
        message.success("Salary updated");
      } else {
        await createEmployeeSalary(payload);
        message.success("Salary added — it now appears on Payroll › Salary Status");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save salary");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Salary Record",
      content: `Delete the ${record.salaryMonthLabel} salary for ${record.payeeName}?`,
      onConfirm: async () => {
        try {
          await deleteEmployeeSalary(record._id);
          message.success("Salary record deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete salary");
        }
      },
    });
  };

  const columns = [
    {
      title: "Payee",
      dataIndex: "payeeName",
      render: (value, r) =>
        r.payeeType === "rider" ? (
          <PartnerLabel name={`${r.partnerId} — ${value}`} deleted={r.riderDeleted} />
        ) : (
          value || "—"
        ),
    },
    {
      title: "Type",
      dataIndex: "payeeType",
      render: (value) => (
        <Tag color={value === "rider" ? "blue" : "default"}>
          {value === "rider" ? "Rider" : "Staff"}
        </Tag>
      ),
    },
    { title: "Salary Month", dataIndex: "salaryMonthLabel" },
    { title: "Worked Days", dataIndex: "workedDays", align: "right" },
    { title: "Basic Pay", dataIndex: "basicPay", align: "right", render: formatOmr },
    { title: "Earnings", dataIndex: "totalEarnings", align: "right", render: formatOmr },
    { title: "Deductions", dataIndex: "totalDeductions", align: "right", render: formatOmr },
    {
      title: "Net Pay (OMR)",
      dataIndex: "netPay",
      align: "right",
      render: (value) => <b>{formatOmr(value)}</b>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value, r) => (
        <Tag color={STATUS_COLORS[value] || "default"}>{r.statusLabel}</Tag>
      ),
    },
    { title: "Bank Account", dataIndex: "sourceBankAccountName" },
  ];

  const moneyInput = (field) => (
    <Col span={8} key={field.name}>
      <Form.Item name={field.name} label={field.label}>
        <InputNumber style={{ width: "100%" }} min={0} precision={3} />
      </Form.Item>
    </Col>
  );

  return (
    <div>
      <KPIGrid minWidth={150}>
        <KPICard label="All" value={kpis.all} />
        <KPICard label="Pending" value={kpis.pending} />
        <KPICard label="Approved" value={kpis.approved} tone="positive" />
        <KPICard label="Transferred" value={kpis.transferred} tone="positive" />
        <KPICard label="On Hold" value={kpis.onHold} />
        <KPICard label="Rejected" value={kpis.rejected} tone="negative" />
        <KPICard label="Net Payable" value={kpis.netPayable} unit="OMR" tone="dark" />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search payee"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Payee Type"
          allowClear
          style={{ width: 160 }}
          options={PAYEE_TYPE_OPTIONS}
          value={payeeType}
          onChange={setPayeeType}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 160 }}
          options={SALARY_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <DatePicker picker="month" format="MM/YYYY" onChange={setPeriod} placeholder="Salary month" />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Employee_Salary"
          onExport={() => exportEmployeeSalaries(params())}
        />
        {canEdit && (
          <>
            <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
              Add Salary
            </Button>
          </>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
          // Approved / transferred rows are committed; reopening is a Payroll decision.
          isEditDisabled: (record) => ["approved", "transferred"].includes(record.status),
          getEditDisabledReason: () =>
            "Approved and transferred salaries cannot be edited",
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Salary" : "Add Salary"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={780}
      >
        <Form form={form} layout="vertical" onValuesChange={recomputeNet}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="payeeType" label="Payee Type" rules={[{ required: true }]}>
                <Select
                  options={PAYEE_TYPE_OPTIONS}
                  onChange={setFormPayeeType}
                  disabled={Boolean(modal.record)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              {formPayeeType === "rider" ? (
                <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    filterOption={false}
                    onSearch={onRiderSearch}
                    loading={ridersLoading}
                    options={riderOptions}
                    placeholder="Search partner"
                    disabled={Boolean(modal.record)}
                  />
                </Form.Item>
              ) : (
                <Form.Item name="employee" label="Employee" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={staffOptions}
                    placeholder="Select employee"
                    disabled={Boolean(modal.record)}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={8}>
              <Form.Item name="period" label="Salary Month" rules={[{ required: true }]}>
                <DatePicker
                  picker="month"
                  format="MM/YYYY"
                  style={{ width: "100%" }}
                  disabled={Boolean(modal.record)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="workedDays" label="Worked Days">
                <InputNumber style={{ width: "100%" }} min={0} precision={1} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Earnings (OMR)
          </Divider>
          <Row gutter={16}>{EARNING_FIELDS.map(moneyInput)}</Row>

          <Divider orientation="left" plain>
            Deductions (OMR)
          </Divider>
          <Row gutter={16}>{DEDUCTION_FIELDS.map(moneyInput)}</Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic title="Total Earnings" value={formatOmr(netPreview.earnings)} />
            </Col>
            <Col span={8}>
              <Statistic title="Total Deductions" value={formatOmr(netPreview.deductions)} />
            </Col>
            <Col span={8}>
              <Statistic
                title="Net Pay"
                value={formatOmr(netPreview.net)}
                valueStyle={{ fontWeight: 700 }}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sourceBankAccount" label="Source Bank Account">
                <Select options={bankOptions} allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMode" label="Payment Mode">
                <Select options={paymentModeOptions} allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <BulkUploadModal
        open={uploadOpen}
        title="Upload Rider Salaries"
        templateName="EZYGO_Employee_Salary_Upload_Template"
        hint="CSV of rider salaries. Office staff are entered individually — matching staff by name from a file is ambiguous."
        extra={
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#6b7280" }}>
              Salary month for every row in this file
            </div>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={uploadPeriod}
              onChange={(value) => setUploadPeriod(value || dayjs())}
              allowClear={false}
              style={{ width: "100%" }}
            />
          </div>
        }
        onDownloadTemplate={downloadSalaryTemplate}
        onUpload={(file) =>
          uploadEmployeeSalaries(file, {
            month: uploadPeriod.month() + 1,
            year: uploadPeriod.year(),
          })
        }
        onClose={() => setUploadOpen(false)}
        onSuccess={fetchAll}
      />
    </div>
  );
}
